import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import {
  mdiMapOutline,
  mdiPlus,
  mdiTableLarge,
} from "@mdi/js";
import { Button, SearchField, Spinner } from "@heroui/react";
import { gasStationsStore } from "@/stores/GasStationsStore";
import { gasStationFormStore } from "@/stores/GasStationFormStore";
import GasStationsTable from "./components/GasStationsTable";
import GasStationsMap from "./components/GasStationsMap";
import GasStationDrawer from "./components/GasStationDrawer";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import "./GasStationsPage.css";

const GasStationsPage: React.FC = observer(() => {
  const [searchInput, setSearchInput] = useState(() => gasStationsStore.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    void gasStationsStore.loadStations();
  }, []);

  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      gasStationsStore.setSearch(searchInput);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const handleRefresh = () => {
    void gasStationsStore.loadStations();
  };

  const handleEdit = (uuid: string) => {
    void gasStationFormStore.openEdit(uuid);
  };

  const handleMapSelect = (uuid: string) => {
    gasStationsStore.selectStation(uuid);
    void gasStationFormStore.openEdit(uuid);
  };

  return (
    <div className="gs-page" data-testid="gas-stations-page">
      <div className="gs-page__toolbar">
        <div className="gs-page__toolbar-left">
          <div className="gs-page__search">
            <SearchField
              value={searchInput}
              onChange={(value) => setSearchInput(value)}
              aria-label="Поиск заправок"
            >
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input placeholder="Поиск..." />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
          </div>

          <div className="gs-page__view-toggle">
            <Button
              size="sm"
              variant={
                gasStationsStore.viewMode === "table" ? "primary" : "tertiary"
              }
              onPress={() => gasStationsStore.setViewMode("table")}
            >
              <Icon path={mdiTableLarge} size={0.7} />
              Таблица
            </Button>
            <Button
              size="sm"
              variant={
                gasStationsStore.viewMode === "map" ? "primary" : "tertiary"
              }
              onPress={() => gasStationsStore.setViewMode("map")}
            >
              <Icon path={mdiMapOutline} size={0.7} />
              Карта
            </Button>
          </div>
        </div>

        <Button
          variant="primary"
          onPress={() => gasStationFormStore.openCreate()}
        >
          <Icon path={mdiPlus} size={0.75} />
          Добавить
        </Button>
      </div>

      <div className="gs-page__content">
        {gasStationsStore.isLoading && gasStationsStore.stations.length > 0 && (
          <div className="gs-page__refresh-indicator">
            <Spinner size="sm" />
          </div>
        )}

        <div
          className="gs-page__table-panel"
          hidden={gasStationsStore.viewMode !== "table"}
        >
          <GasStationsTable onEdit={handleEdit} />
        </div>

        <div
          className="gs-page__map-panel"
          hidden={gasStationsStore.viewMode !== "map"}
        >
          <GasStationsMap
            stations={gasStationsStore.filteredStations}
            selectedUuid={gasStationsStore.selectedUuid}
            isVisible={gasStationsStore.viewMode === "map"}
            onSelectStation={handleMapSelect}
          />
        </div>
      </div>

      <GasStationDrawer onSuccess={handleRefresh} />
      <DeleteConfirmModal />
    </div>
  );
});

GasStationsPage.displayName = "GasStationsPage";
export default GasStationsPage;
